// Shared state for the patient portal — fetched once in the layout
// (`PatientPortal.js`) and exposed to every nested patient page via React
// context. Each sub-page consumes `usePatientData()` instead of re-running
// the supabase queries itself, so navigating between home / appointments /
// meds / vitals / message / labs / chart / wizard doesn't refetch.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getSupabase } from "../../components/supabaseClient";
import { tableName } from "../../components/env";
import { fetchEffectiveUserRow } from "../../utils/effectiveUserRole";
import { filterRowsForPatient } from "../../config/roles";
import { extractAllSubmissionLinks } from "../../utils/submissionUtils";

const PatientDataContext = createContext(null);

export function PatientDataProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [userRow, setUserRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [formsByRow, setFormsByRow] = useState({});
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const supabase = await getSupabase();
      const { data: sessionData } = await supabase.auth.getSession();
      const email = sessionData?.session?.user?.email;
      if (!email) {
        setError("Please log in again.");
        return;
      }
      const { data: user, error: userErr } = await fetchEffectiveUserRow({ email });
      if (userErr || !user) {
        setError("We couldn't load your account.");
        return;
      }
      setUserRow(user);

      const authUserId = sessionData?.session?.user?.id || null;
      const dob = user.patient_dob ? String(user.patient_dob).slice(0, 10) : null;
      const verifiedRowId = user.patient_verified_row_id || null;
      const ehrId =
        user.patient_ehr_id != null && String(user.patient_ehr_id).trim()
          ? String(user.patient_ehr_id).trim()
          : null;
      if (!dob && !verifiedRowId && !authUserId && !ehrId) {
        setRows([]);
        return;
      }

      // Query the public schema and (when known) the patient's verified tenant
      // schema. EHR matching is scoped to the verified schema: EHR numbers are
      // per-schema, so fetching by EHR elsewhere could pull another tenant's
      // patient.
      const clients = [{ client: supabase, isVerifiedSchema: false }];
      if (user.patient_verified_schema) {
        clients.push({
          client: supabase.schema(user.patient_verified_schema),
          isVerifiedSchema: true,
        });
      }

      const queries = [];
      clients.forEach(({ client: c, isVerifiedSchema }) => {
        const orParts = [];
        // EHR is the patient identity — one notes-c row per visit — so this is
        // what surfaces ALL their visits. The dob typed at signup may not match
        // the chart's dob (an admin approved the link anyway), so EHR, not dob,
        // is the reliable key. Uppercase column must be quoted in PostgREST or().
        if (ehrId && isVerifiedSchema) orParts.push(`"EHR_ID".eq.${ehrId}`);
        if (dob) orParts.push(`dob.eq.${dob}`);
        if (verifiedRowId) orParts.push(`id.eq.${verifiedRowId}`);
        if (orParts.length) {
          queries.push(
            c.from(tableName).select("*").or(orParts.join(","))
              .then((res) => res)
              .catch((e) => ({ data: null, error: e }))
          );
        }
        if (authUserId) {
          queries.push(
            c.from(tableName).select("*").eq("user_id", authUserId)
              .then((res) => res)
              .catch((e) => ({ data: null, error: e }))
          );
        }
      });
      const results = await Promise.all(queries);
      results
        .filter((r) => r.error)
        .forEach((r) =>
          console.warn("[PatientPortal] a notes-c query failed:", r.error?.message || r.error)
        );
      if (results.length > 0 && results.every((r) => r.error)) {
        setError("We couldn't load your visits right now.");
        return;
      }

      const byId = new Map();
      results.forEach((r) =>
        (r.data || []).forEach((row) => {
          if (row?.id != null && !byId.has(row.id)) byId.set(row.id, row);
        })
      );
      const fetched = [...byId.values()];

      const filtered = filterRowsForPatient(user, fetched);
      const visible = [...filtered];
      fetched.forEach((row) => {
        const isVerifiedRow =
          verifiedRowId && String(row.id) === String(verifiedRowId);
        const isOwnRow =
          authUserId && String(row.user_id || "") === String(authUserId);
        if ((isVerifiedRow || isOwnRow) && !visible.some((v) => v.id === row.id)) {
          visible.push(row);
        }
      });
      setRows(visible);

      const entries = await Promise.all(
        visible.map(async (row) => {
          try {
            const links = await extractAllSubmissionLinks(row.id);
            return [row.id, Array.isArray(links) ? links : []];
          } catch (_) {
            return [row.id, []];
          }
        })
      );
      setFormsByRow(Object.fromEntries(entries));
    } catch (e) {
      console.error("[PatientPortal] load failed:", e);
      setError("Something went wrong loading your portal.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const value = useMemo(
    () => ({ isLoading, userRow, rows, formsByRow, error, reload: loadData }),
    [isLoading, userRow, rows, formsByRow, error, loadData]
  );

  return (
    <PatientDataContext.Provider value={value}>
      {children}
    </PatientDataContext.Provider>
  );
}

export function usePatientData() {
  const ctx = useContext(PatientDataContext);
  if (!ctx) {
    throw new Error("usePatientData must be used inside PatientDataProvider");
  }
  return ctx;
}

// Pure helpers reused by every patient page.
export function todayString() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function greetingForHour(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function firstNameOf(userRow) {
  const full =
    userRow?.patient_full_name || userRow?.name || userRow?.email || "";
  const first = String(full).trim().split(/[\s,]+/)[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : "there";
}

export function formatLongDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export function formatShortDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = String(timeStr).split(":");
  const hour = parseInt(h, 10);
  if (isNaN(hour)) return timeStr;
  const suffix = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m || "00"} ${suffix}`;
}

export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = Math.round((target - now) / 86400000);
  return isNaN(diff) ? null : diff;
}

export function untilLabel(dateStr) {
  const diff = daysUntil(dateStr);
  if (diff === null) return "Upcoming";
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return `In ${diff} days`;
}

export function doctorLabel(row) {
  const name = (row?.physician_name || "").trim();
  if (!name) return "Your care team";
  return /^dr\.?\s/i.test(name) ? name : `Dr. ${name}`;
}

export function useUpcomingAndPast(rows) {
  return useMemo(() => {
    const today = todayString();
    const up = rows
      .filter((r) => r.date_ && r.date_ >= today)
      .sort((a, b) =>
        `${a.date_} ${a.time_ || ""}`.localeCompare(`${b.date_} ${b.time_ || ""}`)
      );
    const done = rows
      .filter((r) => !r.date_ || r.date_ < today)
      .sort((a, b) =>
        `${b.date_ || ""} ${b.time_ || ""}`.localeCompare(`${a.date_ || ""} ${a.time_ || ""}`)
      );
    return { upcoming: up, past: done };
  }, [rows]);
}